import { db, type UnitRow, type RefrigerationUnitRow } from '../../db';
import { normalizeRefrigerationUnitInput } from './normalization';
import type { RefrigerationUnitInput, RefrigerationUnitUpdateInput } from './types';
import { ReportsRepo } from '../reportsRepo';
import { resolveUnitsPlanningSnapshot } from '@/utils/inspectionPlanning';
import { normalizeIsoDate, normalizeIsoMonth } from '@/utils/date';
import {
  createSyncMetadata,
  softDeleteSyncMetadata,
  updateSyncMetadata,
} from '@/data/sync/syncMetadata';
import { queueForSync } from '@/data/sync/syncOutbox';
import { trackEntityCreated, trackEntityUpdated, trackEntityDeleted } from '@/lib/metrics';

function isActiveUnit(unit: UnitRow): boolean {
  // Use syncDeletedAt as the single source of truth for deletion status
  return !unit.syncDeletedAt;
}

function toUnitRows(units: UnitRow[]): RefrigerationUnitRow[] {
  return units.map((unit) => ({ ...unit }));
}

async function updateSiteUnitCount(siteId: string): Promise<void> {
  const units = await db.units.where('siteId').equals(siteId).toArray();
  const activeCount = units.filter(isActiveUnit).length;
  await db.sites.update(siteId, { refrigerationUnitsCount: activeCount });
}

class RefrigerationUnitsRepoImpl {
  async createRefrigerationUnit(siteId: string, input: RefrigerationUnitInput): Promise<string> {
    return db.transaction('rw', [db.sites, db.units, db.syncOutbox], async () => {
      const site = await db.sites.get(siteId);
      if (!site || site.syncDeletedAt) {
        throw new Error('Stabilimento non trovato');
      }
      const now = new Date().toISOString();
      const existingUnits = await db.units.where('siteId').equals(siteId).toArray();
      const normalized = normalizeRefrigerationUnitInput(
        input,
        now,
        undefined,
        toUnitRows(existingUnits)
      );
      const syncMeta = createSyncMetadata();
      const row: UnitRow = {
        ...normalized,
        siteId,
        companyId: site.companyId,
        ...syncMeta,
      };
      await db.units.add(row);
      await queueForSync('units', row.id, 'upsert', row);
      await updateSiteUnitCount(siteId);
      trackEntityCreated('unit');
      return row.id;
    });
  }

  async updateRefrigerationUnit(
    siteId: string,
    refrigerationUnitId: string,
    input: RefrigerationUnitUpdateInput
  ): Promise<void> {
    await db.transaction('rw', [db.sites, db.units, db.reports, db.syncOutbox], async () => {
      const site = await db.sites.get(siteId);
      if (!site || site.syncDeletedAt) {
        throw new Error('Stabilimento non trovato');
      }
      const units = await db.units.where('siteId').equals(siteId).toArray();
      const target = units.find((unit) => unit.id === refrigerationUnitId);
      if (!target) {
        throw new Error('Impianto non trovato');
      }
      // Use syncDeletedAt as the single source of truth
      if (target.syncDeletedAt) {
        throw new Error('Impianto archiviato');
      }

      const now = new Date().toISOString();
      const hasPlanningInput = ['plannedDate', 'plannedPeriod', 'plannedSource'].some((key) =>
        Object.prototype.hasOwnProperty.call(input, key)
      );
      const normalized = normalizeRefrigerationUnitInput(
        {
          id: refrigerationUnitId,
          refrigerationUnitId: input.refrigerationUnitId ?? target.refrigerationUnitId,
          name: input.name ?? target.name,
          location:
            ((input.location ?? target.location) as
              | RefrigerationUnitInput['location']
              | undefined) ?? undefined,
          ammoniaKg: input.ammoniaKg !== undefined ? input.ammoniaKg : target.ammoniaKg,
          toxicGasType:
            ((input.toxicGasType ?? target.toxicGasType) as
              | RefrigerationUnitInput['toxicGasType']
              | undefined) ?? undefined,
          impiantoType:
            ((input.impiantoType ?? target.impiantoType) as
              | RefrigerationUnitInput['impiantoType']
              | undefined) ?? undefined,
          technical: input.technical,
          plannedDate: input.plannedDate,
          plannedPeriod: input.plannedPeriod,
          plannedSource: input.plannedSource,
        },
        now,
        target,
        toUnitRows(units)
      );
      const syncMeta = updateSyncMetadata(target);
      const nextRow: UnitRow = {
        ...target,
        ...normalized,
        ...syncMeta,
        siteId,
        companyId: site.companyId,
      };

      await db.units.put(nextRow);
      await queueForSync('units', nextRow.id, 'upsert', nextRow);
      trackEntityUpdated('unit');

      const planningChanged =
        normalized.plannedDate !== target.plannedDate ||
        normalized.plannedPeriod !== target.plannedPeriod ||
        normalized.plannedSource !== target.plannedSource;

      if (hasPlanningInput && planningChanged) {
        const draft = await ReportsRepo.getDraftBySite(siteId);
        if (draft) {
          const planningSnapshot = resolveUnitsPlanningSnapshot(
            toUnitRows(units.map((unit) => (unit.id === refrigerationUnitId ? nextRow : unit)))
          );
          const nextMeta = {
            ...draft.meta,
            plannedPeriodSnapshot: planningSnapshot.plannedPeriod,
            plannedDateSnapshot: planningSnapshot.plannedDate,
          };
          await ReportsRepo.upsert({
            ...draft,
            meta: nextMeta,
            updatedAt: now,
          });
        }
      }
    });
  }

  async deleteRefrigerationUnit(siteId: string, refrigerationUnitId: string): Promise<void> {
    await db.transaction('rw', [db.sites, db.units, db.syncOutbox], async () => {
      const site = await db.sites.get(siteId);
      if (!site || site.syncDeletedAt) {
        throw new Error('Stabilimento non trovato');
      }
      const target = await db.units.get(refrigerationUnitId);
      if (!target || target.siteId !== siteId) {
        throw new Error('Impianto non trovato');
      }
      // Use syncDeletedAt as the single source of truth
      if (target.syncDeletedAt) return;

      const syncMeta = softDeleteSyncMetadata(target);
      const nextRow: UnitRow = {
        ...target,
        updatedAt: syncMeta.syncUpdatedAt,
        ...syncMeta,
      };
      await db.units.put(nextRow);
      await queueForSync('units', nextRow.id, 'delete', nextRow);
      await updateSiteUnitCount(siteId);
      trackEntityDeleted('unit');
    });
  }

  async updatePlanningForSite(
    siteId: string,
    updates: {
      plannedPeriod?: string;
      plannedDate?: string;
      plannedSource?: string;
      plannedUpdatedAt?: string;
    }
  ): Promise<void> {
    await db.transaction('rw', [db.sites, db.units, db.reports, db.syncOutbox], async () => {
      const site = await db.sites.get(siteId);
      if (!site || site.syncDeletedAt) {
        throw new Error('Stabilimento non trovato');
      }
      const now = updates.plannedUpdatedAt ?? new Date().toISOString();
      const plannedPeriod = normalizeIsoMonth(updates.plannedPeriod);
      const plannedDate = normalizeIsoDate(updates.plannedDate);
      const plannedSource = updates.plannedSource?.trim() || undefined;

      const units = await db.units.where('siteId').equals(siteId).toArray();
      const previousSnapshot = resolveUnitsPlanningSnapshot(toUnitRows(units));

      const updatedUnits: UnitRow[] = [];
      for (const unit of units) {
        // Use syncDeletedAt as the single source of truth
        if (unit.syncDeletedAt) {
          updatedUnits.push(unit);
          continue;
        }
        const syncMeta = updateSyncMetadata(unit);
        const nextUnit: UnitRow = {
          ...unit,
          plannedPeriod,
          plannedDate,
          plannedSource,
          plannedUpdatedAt: now,
          updatedAt: now,
          ...syncMeta,
        };
        updatedUnits.push(nextUnit);
        await db.units.put(nextUnit);
        await queueForSync('units', nextUnit.id, 'upsert', nextUnit);
      }

      const nextSnapshot = resolveUnitsPlanningSnapshot(toUnitRows(updatedUnits));
      const planningChanged =
        previousSnapshot.plannedDate !== nextSnapshot.plannedDate ||
        previousSnapshot.plannedPeriod !== nextSnapshot.plannedPeriod;
      if (planningChanged) {
        const draft = await ReportsRepo.getDraftBySite(siteId);
        if (draft) {
          await ReportsRepo.upsert({
            ...draft,
            meta: {
              ...draft.meta,
              plannedPeriodSnapshot: nextSnapshot.plannedPeriod,
              plannedDateSnapshot: nextSnapshot.plannedDate,
            },
            updatedAt: now,
          });
        }
      }
    });
  }
}

export const RefrigerationUnitsRepo = new RefrigerationUnitsRepoImpl();
